import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
    fetchChatMessages,
    fetchOlderMessagesWithCallback,
    markChatMessageRead,
} from 'flow/actions/chat-actions';
import { fetchOpenGraphData } from 'flow/actions/open-graph-actions';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    findFirstUnreadMessageKey,
    selectChatMessages,
    selectFailedMessages,
    selectHasReadAllMessages,
    selectLastMessageIsFromTop,
    selectLastMessageIsOwn,
    selectLastMessageKey,
    selectLastMessageRead,
    selectMessagesExist,
} from 'flow/selectors/chat-message-selectors';
import Actions from 'flow/actions';
import { ChatMessageKey, ChatSessionKey } from 'flow/types/EntityKeys';
import { getNewLinksFromMessages } from 'flow/utils/message-utils';
import { useInMeetingExternalMessages } from 'flow/pages/messages/hooks/useInMeetingExternalMessages';
import { mergeDisplayMessages } from './message-ordering';
import { ListItem } from './types';

const SHOW_AVATAR_IF_MESSAGE_IS_AFTER_MINUTES = 3;

export function useMessageListData(chatSessionKey: ChatSessionKey) {
    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const inFocus = useAppSelector((state) => state.app.get('focus'));
    const isSyncing = useAppSelector((state) => state.top.get('isSyncing'));
    const openGraphData = useAppSelector((state) => state.openGraph);
    const hasReadAllMessages = useAppSelector((state) =>
        selectHasReadAllMessages(state, chatSessionKey),
    );

    const messagesExist = useAppSelector((state) =>
        selectMessagesExist(state, chatSessionKey),
    );
    const lastMessageKey = useAppSelector((state) =>
        selectLastMessageKey(state, chatSessionKey),
    );
    const lastMessageRead = useAppSelector((state) =>
        selectLastMessageRead(state, chatSessionKey),
    );
    const lastMessageIsFromTop = useAppSelector((state) =>
        selectLastMessageIsFromTop(state, chatSessionKey),
    );
    const lastMessageIsOwn = useAppSelector((state) =>
        selectLastMessageIsOwn(state, chatSessionKey, ownExtensionKey),
    );

    const latestMessages = useAppSelector((state) =>
        selectChatMessages(state, chatSessionKey),
    );
    const failedMessages = useAppSelector((state) =>
        selectFailedMessages(state, chatSessionKey),
    );
    // Guest messages from the meeting's relay, and only for the meeting's own
    // session - every other chat gets a stable undefined and never subscribes
    // to relay state.
    const externalMessages = useInMeetingExternalMessages(chatSessionKey);

    const { hasFeature } = useFeatureFlags();
    const threadEnabled = !!hasFeature(FeatureFlags.THREADS);
    const chatListIncludes = useMemo(
        () =>
            threadEnabled
                ? ['contact', 'images', 'threads']
                : ['contact', 'images'],
        [threadEnabled],
    );

    const [ready, setReady] = useState(messagesExist);
    const loadingOlderRef = useRef(false);
    // The same as the ref, as state, for the feed's aria-busy.
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    const chatSessionKeyRef = useRef(chatSessionKey);
    const fetchedTailKeyRef = useRef<ChatMessageKey>();

    // Snapshot the unread position as soon as the session's messages are
    // loaded, so the divider stays put while reading instead of climbing away
    // as new messages arrive and get marked read.
    const [snapshotSessionKey, setSnapshotSessionKey] =
        useState<ChatSessionKey>();
    const [snapshotMessageKey, setSnapshotMessageKey] =
        useState<ChatMessageKey>();
    const [firstUnreadMessageKey, setFirstUnreadMessageKey] =
        useState<ChatMessageKey>();

    // While the window is unfocused nothing is being read, so the divider has
    // to follow what arrives - that is the whole "New messages" affordance for
    // someone who stepped away. The session key does not change while they are
    // gone, so snapshotting once per session would never look again.
    const arrivedWhileAway = !inFocus && snapshotMessageKey !== lastMessageKey;

    if (
        messagesExist &&
        (snapshotSessionKey !== chatSessionKey || arrivedWhileAway)
    ) {
        setSnapshotSessionKey(chatSessionKey);
        setSnapshotMessageKey(lastMessageKey);
        setFirstUnreadMessageKey(
            findFirstUnreadMessageKey(latestMessages, ownExtensionKey),
        );
    }

    // Build flat item list from messages
    const items: ListItem[] = useMemo(() => {
        const sorted = mergeDisplayMessages(
            latestMessages,
            failedMessages,
            externalMessages,
        );
        if (sorted.isEmpty()) return [];

        const result: ListItem[] = [];
        let prevDate: string | undefined;
        let prevFromKey: string | undefined;
        let prevSent: string | undefined;
        let prevType: string | undefined;

        sorted.forEach((message) => {
            const key = message.get('key');
            const sent = message.get('sent');
            // A message pushed over Top is assembled from the notification and
            // has no type; only the fetched copy names it 'message'. Reading it
            // as untyped would break the sender-run check below, so the newest
            // message would carry its own avatar until the refetch replaced it.
            const type = message.get('type') ?? 'message';
            // Guests have no extension, so they are identified by name.
            const fromKey = (
                type === 'external'
                    ? message.getIn(['from', 'name'])
                    : message.getIn(['from', 'key'])
            ) as string;
            const date = moment(sent).format('YYYY-MM-DD');

            if (date !== prevDate) {
                result.push({ type: 'date-divider', date });
                prevFromKey = undefined;
                prevSent = undefined;
                prevType = undefined;
            }

            if (firstUnreadMessageKey && key === firstUnreadMessageKey) {
                result.push({ type: 'unread-divider' });
            }

            if (type === 'post') {
                result.push({ type: 'post', chatMessageKey: key });
                prevFromKey = undefined;
                prevSent = undefined;
                prevType = undefined;
            } else {
                // A guest and a colleague never continue each other's run, even
                // if the name and the key happen to coincide.
                const sameSender =
                    prevType !== 'post' &&
                    prevType === type &&
                    prevFromKey === fromKey &&
                    prevFromKey !== undefined;
                const withinTimeGap =
                    prevSent !== undefined &&
                    moment(sent).diff(moment(prevSent), 'minutes') <
                        SHOW_AVATAR_IF_MESSAGE_IS_AFTER_MINUTES;
                const showAvatar =
                    !sameSender ||
                    !withinTimeGap ||
                    key === firstUnreadMessageKey;

                result.push(
                    type === 'external'
                        ? { type: 'external', message, showAvatar }
                        : { type: 'message', chatMessageKey: key, showAvatar },
                );
                prevFromKey = fromKey;
                prevSent = sent;
                prevType = type;
            }

            prevDate = date;
        });

        return result;
    }, [
        latestMessages,
        failedMessages,
        externalMessages,
        firstUnreadMessageKey,
    ]);

    // Detect chat switch synchronously during render
    if (chatSessionKeyRef.current !== chatSessionKey) {
        chatSessionKeyRef.current = chatSessionKey;
        setReady(messagesExist);
        loadingOlderRef.current = false;
        setIsLoadingOlder(false);
        fetchedTailKeyRef.current = undefined;
    }

    // Newest message held in fetched form, and so the anchor a pushed message
    // asks from. Tracked as the tail is seen rather than when a fetch goes out:
    // the messages that arrive without prompting a fetch of their own - the
    // page that loads when the chat opens, above all - still move the anchor,
    // and without that the first pushed message would have nothing to anchor on
    // and would pull the whole page back.
    if (!lastMessageIsFromTop && lastMessageKey) {
        fetchedTailKeyRef.current = lastMessageKey;
    }

    // Tell the app which chat is on screen, so the reconnect handler knows to
    // refill this list. Messages that arrive while the connection is down are
    // never pushed, and the session list that arrives on reconnect carries only
    // the newest of them - so without this the ones in between stay missing
    // until the chat is reopened.
    useEffect(() => {
        dispatch({
            type: Actions.SET_MOUNTED_CHAT_SESSION,
            chatSessionKey,
        });
        return () => {
            dispatch({
                type: Actions.SET_MOUNTED_CHAT_SESSION,
                chatSessionKey: null,
            });
        };
    }, [chatSessionKey, dispatch]);

    // Fetch on chat session change, and again whenever the tail message came
    // over Top. The notification carries no stylings, so a mention in a pushed
    // message stays plain text until the fetched copy replaces it. Fetching
    // once per tail key keeps this from repeating if the replacement still
    // carries the Top marker.
    const fetchedRef = useRef<{
        chatSessionKey?: ChatSessionKey;
        lastMessageKey?: ChatMessageKey;
    }>({});

    useEffect(() => {
        const sessionChanged =
            fetchedRef.current.chatSessionKey !== chatSessionKey;
        const topMessageArrived =
            lastMessageIsFromTop &&
            fetchedRef.current.lastMessageKey !== lastMessageKey;
        if (!sessionChanged && !topMessageArrived) return;

        // Ask only for what arrived after the tail we already hold, so a
        // pushed message costs one message rather than a whole page - and
        // leaves every other stored message untouched, which is what keeps
        // the rest of the list from re-rendering. Opening a session has no
        // such anchor and wants the page.
        const since = sessionChanged
            ? null
            : (fetchedTailKeyRef.current ?? null);

        fetchedRef.current = { chatSessionKey, lastMessageKey };
        dispatch(
            fetchChatMessages(
                chatSessionKey,
                since,
                false,
                ...chatListIncludes,
            ),
        );
    }, [
        chatSessionKey,
        lastMessageKey,
        lastMessageIsFromTop,
        chatListIncludes,
        dispatch,
    ]);

    // Mark as ready once messages arrive
    useEffect(() => {
        if (!ready && messagesExist) {
            setReady(true);
        }
    }, [messagesExist, ready]);

    // Mark messages as read when at bottom and window in focus
    const markReadIfNeeded = useCallback(() => {
        if (!ready || !lastMessageKey || !inFocus) return;
        if (!lastMessageRead && !hasReadAllMessages) {
            dispatch(markChatMessageRead(chatSessionKey, lastMessageKey));
        }
    }, [
        ready,
        lastMessageKey,
        lastMessageRead,
        inFocus,
        chatSessionKey,
        hasReadAllMessages,
        dispatch,
    ]);

    // Mark read on focus change
    useEffect(() => {
        markReadIfNeeded();
    }, [markReadIfNeeded]);

    // Fetch OpenGraph data for new links
    useEffect(() => {
        if (!isSyncing && latestMessages && openGraphData) {
            const urlsToFetch = getNewLinksFromMessages(
                latestMessages,
                openGraphData,
            );
            if (urlsToFetch && !urlsToFetch.isEmpty()) {
                dispatch(fetchOpenGraphData(urlsToFetch));
            }
        }
    }, [dispatch, isSyncing, latestMessages, openGraphData]);

    const loadOlderMessages = useCallback(() => {
        if (loadingOlderRef.current) return;
        if (!latestMessages || latestMessages.size === 0) return;

        loadingOlderRef.current = true;
        setIsLoadingOlder(true);
        const firstKey = latestMessages.first().get('key');
        dispatch(
            fetchOlderMessagesWithCallback(
                chatSessionKeyRef.current,
                firstKey,
                () => {
                    loadingOlderRef.current = false;
                    setIsLoadingOlder(false);
                },
                ...chatListIncludes,
            ),
        );
    }, [latestMessages, chatListIncludes, dispatch]);

    return {
        items,
        ready,
        lastMessageIsOwn,
        markReadIfNeeded,
        loadOlderMessages,
        isLoadingOlder,
    };
}
